import Button from 'components/Form/Button';
import BasicTable from 'components/Form/Table';
import { Icon } from 'components/Icon';
import TableWrapper from 'components/TableWrapper';
import ContainerAuthenticated from 'containers/Authenticated';
import { CoreContext } from 'context/CoreContext';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { toast } from 'react-toastify';
import { GET } from 'services/api';
import { Icon as OldIcon, StatusContent, Title } from 'ui/styled';
import { normalizeResponse, selectColors } from 'utils';
import { formatElapsedTime, parseCurrency } from 'utils/parsers';
import { formatadores, normalizeName } from 'utils/validators';

export default function PreproposalApproved() {
  const history = useHistory();
  const navigate = useCallback((to) => history.push(`/${to}`), [history]);
  const { user, filterExpression } = useContext(CoreContext);
  const [loading, setLoading] = useState(false);
  const [registers, setRegisters] = useState([]);
  const [tick, setTick] = useState(0);
  const opcao = 'preaprovado';

  // Atualiza a cada 2 minutos
  useEffect(() => {
    const interval = setInterval(() => setTick((prev) => prev + 1), 120000);
    return () => clearInterval(interval);
  }, []);

  const init = useCallback(async () => {
    setLoading(true);
    try {
      const populate = 'distribuidor,integrador,banco,uf,bancos';
      const url = `/pre-proposals?populate=${populate}&filters[status]=aprovado&filters[proposta_financeira][id][$null]=true`;
      const result = await GET(url, true);
      const data = normalizeResponse(result);
      if (!data.length) {
        setRegisters([]);
        setLoading(false);
        return;
      }
      const sorted = data.sort((a, b) => (b.id || 0) - (a.id || 0));
      const next = sorted.map((item) => ({
        ...item,
        accepted: (item.bancos || []).find((f) => f.aceito),
      }));
      setRegisters(next);
    } catch (err) {
      console.error('Erro ao carregar pré-propostas:', err);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  const columns = useMemo(() => {
    const baseColumns = [
      { title: 'Id', sortable: true, ref: 'id' },
      {
        title: 'Data',
        titleAlign: 'center',
        sortable: true,
        ref: 'date',
        renderCell: ({ row }) => formatadores.TIMESTAMP_TO_BR(row.date),
      },
      {
        title: 'Aprovado há',
        titlealign: 'center',
        align: 'center',
        renderCell: ({ row }) => {
          const diffDays = formatadores.DIFF_DAYS_NOW(row?.date);
          return (
            <span style={{ color: diffDays >= 20 ? 'red' : 'inherit' }}>
              {formatElapsedTime(row?.date)}
            </span>
          );
        },
      },
      // Inserir coluna "Distribuidora" condicionalmente
      ...(user?.administrador ||
      (user?.distribuidor && user?.distribuidores?.length > 0)
        ? [
            {
              title: 'Distribuidora',
              sortable: true,
              ref: 'distributor',
            },
          ]
        : []),
      { title: 'Cliente', sortable: true, ref: 'customer' },
      { title: 'Valor', ref: 'value', renderCell: ({ row }) => row?.value },
      { title: 'Integrador', sortable: true, ref: 'nome_integrador' },
      { title: 'Vendedor', sortable: true, ref: 'vendedor' },
      {
        title: 'Telefone Vendedor',
        renderCell: ({ row }) => {
          if (!row?.telefone) return '-';
          const numeroLimpo = row.telefone.replace(/\D/g, '');
          const linkWhats = `https://wa.me/55${numeroLimpo}`;
          return (
            <a
              href={linkWhats}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'blue',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              {row.telefone}
            </a>
          );
        },
      },
      {
        title: 'Banco',
        sortable: true,
        ref: 'bancoAproved',
        renderCell: ({ row }) => row?.bancoAproved,
      },
      {
        title: 'TAG',
        ref: 'status',
        renderCell: ({ row }) => (
          <StatusContent
            color={selectColors(row?.status)}
            style={{ cursor: 'pointer' }}
            onClick={() => {
              if (row?.status === 'Escolha Banco') {
                //envia para pagina para selecionar o banco.
                navigate(`financingtablepreanalise/${row?.id}/${opcao}`);
              } else {
                //envia para criar a proposta
                navigate(`proposals/financing-nova/${row?.id}`);
              }
            }}
          >
            {row?.status}
          </StatusContent>
        ),
      },
      // Coluna do Espelho da Proposta - só aparece para administradores
      ...(user?.administrador
        ? [
            {
              title: 'Espelho da Proposta',
              ref: 'espelho',
              renderCell: ({ row }) => (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Button
                    onClick={() => navigate(`financingproposal/${row?.id}`)}
                    color={'blue'}
                    nospace
                    mbutton
                    small
                  >
                    Visualizar
                  </Button>
                </div>
              ),
            },
          ]
        : []),
      {
        title: 'Link E-Proposta',
        renderCell: ({ row }) =>
          row?.url_token &&
          row?.status === 'Gerar Proposta' && (
            <Button
              color="orange"
              onClick={async () => {
                const copy_url = `${window.location.origin}/pre-proposals/getpretoken/${row.url_token}`;
                try {
                  await navigator.clipboard.writeText(copy_url);
                  toast.success(
                    'E-Proposta copiada para área de transferência.'
                  );
                } catch (err) {
                  toast.error('Não foi possível copiar E-Proposta');
                  console.error(err);
                }
              }}
              style={{
                padding: '6px',
              }}
              nospace
              mbutton
              small
            >
              <Icon name="content_copy" size={18} />
            </Button>
          ),
      },
      {
        title: '',
        renderCell: ({ row }) => {
          return (
            <OldIcon
              icon="arrow-right"
              style={{ cursor: 'pointer' }}
              onClick={() =>
                navigate(`financingtablepreanalise/${row?.id}/${opcao}`)
              }
            />
          );
        },
      },
    ];
    return baseColumns;
  }, [user, navigate]);

  const rows = useMemo(() => {
    return (registers || []).map((a) => ({
      id: a?.id,
      date: a?.data_aprovado,
      formatedDate: formatadores.TIMESTAMP_TO_BR(a?.data_aprovado),
      distributor: normalizeName(a?.distribuidor?.trade_name) || '-',
      customer: normalizeName(a?.nome_completo) || '-',
      value: parseCurrency(a?.valor_financiamento),
      status: a?.bancos?.find((banco) => banco.aceito === true)
        ? normalizeName('Gerar Proposta')
        : normalizeName('Escolha Banco'),
      bancoAproved:
        normalizeName(a?.bancos?.find((b) => b.aceito)?.banco.razao_social) ||
        '-',
      telefone: a?.telefone_vendedor || null,
      vendedor: normalizeName(a?.nome_vendedor) || '-',
      nome_integrador: a?.nome_integrador || '-',
      cnpj_integrador: a?.cnpj_integrador,
      cnpj_integrador_clean: a?.cnpj_integrador.replace(/\D/g, ''),
      cpfCnpj: a?.cpf || a?.cnpj,
      cpf_cnpj_clean: a?.cpf
        ? a?.cpf.replace(/\D/g, '')
        : a?.cnpj.replace(/\D/g, ''),
      url_token: a?.url_token,
    }));
  }, [registers]);

  useEffect(() => init(), [init, tick]);

  return (
    <ContainerAuthenticated>
      <Title>Pré-Financiamento</Title>
      <TableWrapper title="Pré-Aprovada" search>
        <BasicTable
          loading={loading}
          columns={columns}
          rows={rows?.filter(filterExpression)}
          pagination
        />
      </TableWrapper>
    </ContainerAuthenticated>
  );
}
